import { Component, Input } from '@angular/core';

import { IUserAvatar } from '@common/models/user-info.interface';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosDefaultProfileBadge, IEidosIcon } from '@common/config/environment.interface';
import { EidosUtilityService } from '@common/services/eidos-utility.service';
import { EidosIconType, EidosLogSeverity } from '@common/models/core-constant.model';
import { EidosLogService } from '@common/services/eidos-log.service';
import { SpinnerOverlayService } from '@app/core/services/spinner-overlay.service';
import { EidosBaseComponent } from '../eidos-base.component';
import { takeUntil } from 'rxjs';
import { EidosSecurityService } from '@app/core/services/eidos-security.service';

@Component({
  selector: 'eidos-user-avatar',
  templateUrl: './eidos-user-avatar.component.html',
  styleUrls: ['./eidos-user-avatar.component.scss']
})
export class EidosUserAvatarComponent extends EidosBaseComponent {

  @Input()
  public userAvatar: IUserAvatar | undefined;

  public defaultProfileBadge: IEidosDefaultProfileBadge = {
    defaultBadgeColorPrimary: "",
    defaultBadgeColorSecondary: ""
  };
  /**
   * Eidos debug mode flag
   *
   * @type {boolean}
   * @memberof EidosUserAvatarComponent
   */
  public inDebugMode: boolean = false;
  /**
   * Debug icon configuration
   *
   * @type {IEidosIcon}
   * @memberof EidosUserAvatarComponent
   */
  public debugIcon: IEidosIcon = {
    iconType: EidosIconType.Awesome,
    iconCode: "fab fa-android",
    iconSize: 1
  }
  /**
   * End Impersonate icon configuration
   *
   * @type {IEidosIcon}
   * @memberof EidosUserAvatarComponent
   */
  public userImpersonationIcon: IEidosIcon = {
    iconType: EidosIconType.Awesome,
    iconCode: "fa-circle-user",
    iconSize: 1
  }

  apiEnv = ''

  constructor(
    private eidosConfigService: EidosConfigService
    , private utilityService: EidosUtilityService
    , private eidosLogService: EidosLogService
    , private eidosSecurityService: EidosSecurityService
    , private spinnerOverlayService: SpinnerOverlayService
  ) {
    super()

    if(this.eidosSecurityService.isDeveloperUser()) {
      if(this.utilityService.isOnByApiLevel('localhost')) {
        this.apiEnv = 'API: local'
      } else if(this.utilityService.isOnByApiLevel('test')) {
        this.apiEnv = 'API: test'
      } else if(this.utilityService.isOnByApiLevel('uat')) {
        this.apiEnv = 'API: UAT'
      } else if(this.utilityService.isOnByApiLevel('training')) {
        this.apiEnv = 'API: training'
      } else if(this.utilityService.isOnByApiLevel('prod')) {
        this.apiEnv = 'API: Production'
      }
    }
  }

  ngOnInit(): void {
    this.eidosConfigService.currentConfig
      .pipe(takeUntil(this.subscription))
      .subscribe(config => this.defaultProfileBadge = config.defaultProfileBadge);
    this.utilityService.inDebugMode
      .pipe(takeUntil(this.subscription))
      .subscribe(debug => this.inDebugMode = debug);
  }

  public getBadgePrimaryColor(): string {
    if (this.userAvatar?.badgeColorPrimary) {
      return this.userAvatar.badgeColorPrimary;
    } else {
      return this.defaultProfileBadge.defaultBadgeColorPrimary;
    }
  }

  public getBadgeSecondaryColor(): string {
    if (this.userAvatar?.badgeColorSecondary) {
      return this.userAvatar.badgeColorSecondary;
    } else {
      return this.defaultProfileBadge.defaultBadgeColorSecondary;
    }
  }

  /**
   * Return user profile picture URL
   *
   * @return {*}  {string}
   * @memberof EidosUserAvatarComponent
   */
  public getProfileImage(): string {
    return `url('${this.userAvatar!.profilePicture}')`;
  }

  /**
   * End user impersionation, handler of impersonate icon
   *
   * @memberof EidosUserAvatarComponent
   */
  public endImpersonation(): void {
    this.eidosLogService.logDebug(EidosLogSeverity.Log, 'Impersonate', 'Trying to end impersonate user.');
    this.spinnerOverlayService.show();
    this.utilityService.impersonateEnd().subscribe((result) => {
      if (result) {
        this.eidosLogService.logDebug(EidosLogSeverity.Log, 'Impersonate', 'Successfully end impersonate');
      }
      else {
        this.eidosLogService.logDebug(EidosLogSeverity.Log, 'Impersonate', 'Failed to end impersonate');
      }
      this.spinnerOverlayService.hide();
    });
  }

  /**
   * End the debug mode, handler of debug icon
   *
   * @memberof EidosUserAvatarComponent
   */
  public endDebugMode(): void {
    this.utilityService.setDebugMode(false);
  }
}
